import { slugify } from '@schuelerhilfe/shared/src/utils/slugify';
import type { ContextEntities } from './RenderContext';
import { regionBySlug } from '@/lib/countries';

const BUNDESLAND_TAG_PREFIX = 'bundesland:';

/** The federal state a render is about, as `{state.name}` / `{state.slug}`. */
export interface ResolvedState {
  name: string;
  slug: string;
}

/**
 * Resolves the federal state for `{state.*}` placeholders
 * (30.03.03-Widget-Rendering § Placeholder Variables).
 *
 * A Bundesland has no Strapi collection (10.02.07 § AC 3), so the value comes
 * from one of two places, in this order:
 *
 * 1. **The Location entity.** `address.state.name` is authoritative — the
 *    `bundesland:` tag is `slugify()`d *from* it by the location import, so the
 *    entity holds the original spelling even for a state `countries.ts` lacks.
 * 2. **The request tag bag** plus the slug→name table in `lib/countries.ts` —
 *    the only source on a Federal State Page, where no entity exists.
 *
 * Both are per-request values, never `process.env`: this runs in client
 * components too, where non-`NEXT_PUBLIC_` vars like `BRAND` are undefined and
 * every brand would resolve to the default one after hydration.
 */
export function resolveState(
  entities: ContextEntities,
  tagBag: readonly string[],
): ResolvedState | undefined {
  const taggedSlug = tagBag
    .find((tag) => tag.startsWith(BUNDESLAND_TAG_PREFIX))
    ?.slice(BUNDESLAND_TAG_PREFIX.length);

  // Covers a Location carrying a state but no `bundesland:` tag (partially
  // synced data); `slugify` is the very function that writes the tag.
  const entityName = entities.location?.address?.state?.name;
  if (entityName) {
    return { name: entityName, slug: taggedSlug ?? slugify(entityName) };
  }

  if (!taggedSlug) {
    return undefined;
  }

  const region = regionBySlug(taggedSlug);
  return region ? { name: region.name, slug: taggedSlug } : undefined;
}
